import { useMemo, useState, type ReactNode } from 'react';
import { Button, Modal } from 'antd';
import { HelpCircle } from 'lucide-react';
import { Link } from 'react-router';
import type { ArrangementType, TemplateDecisionClass, TemplateListItemDto, TemplateSetDto } from '@pams/shared';
import { templateDecisionHref } from '@/lib/templateLinks';
import { LIBRARY_COPY, OPEN_DECISIONS_COPY, SETUP_GUIDE_COPY, TEMPLATES_COPY } from '../copy';
import { useTemplateCalendar } from '../templateQueries';
import { SETUP_STEPS, setupFiguresOf, undatedDaysOf, type SetupFigures, type SetupStep } from './setupGuide';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7C §3] THE SETUP GUIDE — the library's first header action: what to do, in the order that makes
 *  each next step possible, each step with this set's own figures and a link to where it is done.
 *
 *  ⚠️ IT DECIDES NOTHING AND IT COUNTS ALMOST NOTHING. The decision figures are the server's, the
 *  waiting markers and "no single decision…" the panel's own (`setupGuide.ts` says why), and every
 *  number on it is LIVE — never a figure from the 2025 booklet written into a sentence.
 *
 *  ⚠️ A CONTROLLED `Modal`, `destroyOnHidden`, AND IT READS NOTHING UNTIL IT IS OPENED: the calendar it
 *  states the undated festival days from is fetched on open, so the library's own request census does
 *  not move. Swept by `e2e/fixtures/overlays.ts`, in the same commit as this file.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
type DecisionStep = Extract<SetupStep, TemplateDecisionClass>;

const DECISION_COUNT: Record<DecisionStep, (outstanding: number, templates: number) => string> = {
  SCHEDULE: SETUP_GUIDE_COPY.SCHEDULE.count,
  FORCE_COLUMN: SETUP_GUIDE_COPY.FORCE_COLUMN.count,
  POLICY: SETUP_GUIDE_COPY.POLICY.count,
  SOURCE: SETUP_GUIDE_COPY.SOURCE.count,
};

const isDecisionStep = (step: SetupStep): step is DecisionStep => step !== 'CREATE' && step !== 'PUBLISH';

/** A decision step's figures: its own class's count, or its done sentence, and what else it waits on. */
const decisionLines = (step: DecisionStep, figures: SetupFigures): string[] => {
  const { outstanding, templatesAffected } = figures.classes[step];
  const lead = outstanding > 0 ? DECISION_COUNT[step](outstanding, templatesAffected) : SETUP_GUIDE_COPY[step].done;
  if (step === 'POLICY') {
    const roles = figures.classes.IN_CHARGE_ROLE.outstanding;
    return [
      lead,
      ...(figures.waiting.POLICY > 0 ? [OPEN_DECISIONS_COPY.waiting.POLICY(figures.waiting.POLICY)] : []),
      ...(figures.waiting.IN_CHARGE_ROLE > 0
        ? [OPEN_DECISIONS_COPY.waiting.IN_CHARGE_ROLE(figures.waiting.IN_CHARGE_ROLE)]
        : []),
      ...(roles > 0 ? [SETUP_GUIDE_COPY.POLICY.roles(roles)] : []),
    ];
  }
  if (step === 'SOURCE') {
    const along = SETUP_GUIDE_COPY.SOURCE.alongTheWay(
      figures.classes.TOTALS.outstanding,
      figures.classes.DUTY_POINT.outstanding,
    );
    return [lead, ...(along === '' ? [] : [along])];
  }
  return [lead];
};

const Step = ({
  step,
  index,
  title,
  body,
  lines,
  link,
}: {
  step: SetupStep;
  index: number;
  title: string;
  body: string;
  lines: readonly string[];
  link: ReactNode;
}): React.ReactElement => (
  <li className="flex flex-col gap-1 py-3" data-step={step} data-testid="setup-step">
    <h3 className="font-medium text-heading">{`${String(index + 1)}. ${title}`}</h3>
    <p>{body}</p>
    <div className="flex flex-col gap-0.5 text-muted-foreground" data-testid={`setup-count-${step}`}>
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
    {link}
  </li>
);

export const SetupGuide = ({
  setKey,
  set,
  items,
  rareColumns,
  year,
  type,
}: {
  setKey: string;
  set: TemplateSetDto;
  items: readonly TemplateListItemDto[];
  rareColumns: ReadonlyMap<string, number>;
  /** The year the library is creating for, from the registered types — `null` when none is registered. */
  year: number | null;
  type: ArrangementType | undefined;
}): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const calendar = useTemplateCalendar(setKey, year ?? 0, { enabled: open && year !== null });
  const figures = useMemo(() => setupFiguresOf(set.decisions, items, rareColumns), [set.decisions, items, rareColumns]);

  const createLines = (): string[] => {
    if (year === null) return [SETUP_GUIDE_COPY.CREATE.ready(figures.ready)];
    const dated = calendar.data ? undatedDaysOf(calendar.data) : null;
    return [
      SETUP_GUIDE_COPY.CREATE.ready(figures.ready),
      dated === null
        ? calendar.isPending
          ? SETUP_GUIDE_COPY.CREATE.readingDates
          : ''
        : SETUP_GUIDE_COPY.CREATE.undated(dated.undated, dated.days, year),
    ].filter((line) => line !== '');
  };

  const stepOf = (step: SetupStep, index: number): React.ReactElement => {
    if (isDecisionStep(step)) {
      return (
        <Step
          key={step}
          step={step}
          index={index}
          title={SETUP_GUIDE_COPY[step].title}
          body={SETUP_GUIDE_COPY[step].body}
          lines={decisionLines(step, figures)}
          link={
            <Link to={templateDecisionHref(setKey, step)} className="text-link" data-testid={`setup-link-${step}`}>
              {SETUP_GUIDE_COPY[step].link}
            </Link>
          }
        />
      );
    }
    const noType = <p className="text-muted-foreground">{TEMPLATES_COPY.noTypeForEvent}</p>;
    if (step === 'CREATE') {
      return (
        <Step
          key={step}
          step={step}
          index={index}
          title={SETUP_GUIDE_COPY.CREATE.title}
          body={SETUP_GUIDE_COPY.CREATE.body(year, LIBRARY_COPY.selectAllReady, LIBRARY_COPY.plan)}
          lines={createLines()}
          link={
            year === null ? (
              noType
            ) : (
              <Link
                to={`/templates/${setKey}/calendar/${String(year)}`}
                className="text-link"
                data-testid="setup-link-CREATE"
              >
                {SETUP_GUIDE_COPY.CREATE.link(year)}
              </Link>
            )
          }
        />
      );
    }
    return (
      <Step
        key={step}
        step={step}
        index={index}
        title={SETUP_GUIDE_COPY.PUBLISH.title}
        body={SETUP_GUIDE_COPY.PUBLISH.body(LIBRARY_COPY.publish)}
        lines={[SETUP_GUIDE_COPY.PUBLISH.drafts(figures.drafts)]}
        link={
          type === undefined ? (
            noType
          ) : (
            <Link
              to={`/templates/${setKey}/publish?type=${type}`}
              className="text-link"
              data-testid="setup-link-PUBLISH"
            >
              {LIBRARY_COPY.publish}
            </Link>
          )
        }
      />
    );
  };

  return (
    <>
      <Button
        icon={<HelpCircle className="size-4" aria-hidden />}
        data-testid="library-setup-guide"
        onClick={() => {
          setOpen(true);
        }}
      >
        {SETUP_GUIDE_COPY.trigger}
      </Button>
      <Modal
        open={open}
        title={SETUP_GUIDE_COPY.title}
        footer={null}
        width={640}
        centered
        destroyOnHidden
        onCancel={() => {
          setOpen(false);
        }}
      >
        <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1 text-sm" data-testid="setup-guide">
          <p>{SETUP_GUIDE_COPY.intro}</p>
          <p className="font-medium text-heading" data-testid="setup-headline">
            {LIBRARY_COPY.decisionsHeadline(figures.outstanding, figures.templatesAffected)}
          </p>
          {figures.noneAlone ? (
            <p className="text-muted-foreground" data-testid="setup-no-single-decision">
              {SETUP_GUIDE_COPY.noSingleDecision}
            </p>
          ) : null}
          <ol className="flex flex-col divide-y divide-border" aria-label={SETUP_GUIDE_COPY.steps}>
            {SETUP_STEPS.map(stepOf)}
          </ol>
        </div>
      </Modal>
    </>
  );
};
